import ShiftService from '@src/services/ShiftService';
import { translateString } from '@src/util/intl/translateString';

import { SHIFT_TYPES } from '../constants';
import type { ShiftValidationConfig } from '../types';

/**
 * Configuration for different shift types validation rules
 */
export const SHIFT_VALIDATION_CONFIG: Record<string, ShiftValidationConfig> = {
  [SHIFT_TYPES.FIXED]: {
    shiftType: SHIFT_TYPES.FIXED,
    maxWeeklyHours: ShiftService.MAX_WEEKLY_HOURS,
    validateMaxHoursAsError: false,
    maxHours: ShiftService.MAX_FIXED_HOURS,
    maxDailyHours: ShiftService.MAX_FIXED_DAILY_HOURS,
    maxDays: ShiftService.MAX_DAYS,
    messages: {
      warnings: {
        maxDailyHours: {
          message: translateString('shifts.validation.hours.maxDailyHours.warning'),
        },
        maxHours: {
          message: translateString('shifts.validation.hours.maxHours.warning', {
            hours: ShiftService.MAX_FIXED_HOURS,
          }),
        },
        maxDays: {
          message: translateString('shifts.validation.workdays.warning', {
            days: ShiftService.MAX_DAYS,
          }),
        },
      },
      errors: {
        breakTime: {
          message: translateString('shift.mealBreak.required'),
        },
      },
    },
  },
  [SHIFT_TYPES.PART_TIME]: {
    shiftType: SHIFT_TYPES.PART_TIME,
    maxWeeklyHours: ShiftService.MAX_WEEKLY_HOURS_PART_TIME,
    validateMaxHoursAsError: true,
    maxHours: ShiftService.MAX_WEEKLY_HOURS_PART_TIME,
    maxDailyHours: ShiftService.MAX_PART_TIME_DAILY_HOURS,
    maxDays: ShiftService.MAX_DAYS,
    messages: {
      warnings: {
        maxDailyHours: {
          message: translateString('shifts.validation.hours.maxDailyHoursPartTime'),
        },
        maxHours: {
          message: translateString('shifts.validation.hours.maxHours.warning', {
            hours: ShiftService.MAX_FIXED_HOURS,
          }),
        },
        maxDays: {
          message: translateString('shifts.validation.workdays.warning', {
            days: ShiftService.MAX_DAYS,
          }),
        },
      },
      errors: {
        maxHours: {
          message: translateString('shifts.validation.hours.maxHoursPartTime.error'),
        },
        breakTime: {
          message: translateString('shift.mealBreak.required'),
        },
      },
    },
  },
  [SHIFT_TYPES.CYCLE]: {
    shiftType: SHIFT_TYPES.CYCLE,
    maxWeeklyHours: ShiftService.MAX_WEEKLY_HOURS,
    validateMaxHoursAsError: false,
    maxDays: ShiftService.MAX_DAYS,
    messages: {
      warnings: {
        maxDays: {
          message: translateString('shifts.validation.workdays.warning', {
            days: ShiftService.MAX_DAYS,
          }),
        },
      },
      errors: {},
    },
  },
  [SHIFT_TYPES.BIWEEKLY]: {
    shiftType: SHIFT_TYPES.BIWEEKLY,
    maxWeeklyHours: ShiftService.MAX_WEEKLY_HOURS,
    validateMaxHoursAsError: false,
    maxDays: ShiftService.MAX_DAYS,
    maxHours: ShiftService.MAX_BIWEEKLY_HOURS,
    maxDailyHours: ShiftService.MAX_BIWEEKLY_DAILY_HOURS,
    maxDailyHoursWarning: ShiftService.MAX_BIWEEKLY_DAILY_HOURS_WARNING,
    messages: {
      warnings: {
        maxDailyHours: {
          message: translateString('shifts.validation.hours.maxDailyHours.warning'),
        },
        maxConsecutiveActiveDays: {
          message: translateString('shifts.validation.maxConsecutiveActiveDays.warning'),
        },
        maxDays: {
          message: translateString('shifts.validation.workdays.warning', {
            days: ShiftService.MAX_DAYS,
          }),
        },
      },
      errors: {
        maxHours: {
          message: translateString('shifts.validation.hours.maxHoursCycle.error'),
        },
        breakTime: {
          message: translateString('shift.mealBreak.required'),
        },
      },
    },
  },
  [SHIFT_TYPES.SHIFT_WORK]: {
    shiftType: SHIFT_TYPES.SHIFT_WORK,
    maxWeeklyHours: ShiftService.MAX_WEEKLY_HOURS,
    validateMaxHoursAsError: false,
    maxDays: ShiftService.MAX_DAYS,
    maxDailyHours: ShiftService.MAX_WORKDAY_DAILY_HOURS,
    messages: {
      warnings: {
        maxDays: {
          message: translateString('shifts.validation.workdays.warning', {
            days: ShiftService.MAX_DAYS,
          }),
        },
        maxDailyHours: {
          message: translateString('shifts.validation.hours.maxDailyHours.warning'),
        },
      },
      errors: {},
    },
  },
};

/**
 * Get validation configuration for a specific shift type
 */
export const getShiftValidationConfig = (shiftType: string): ShiftValidationConfig => {
  return SHIFT_VALIDATION_CONFIG[shiftType] || SHIFT_VALIDATION_CONFIG.fixed;
};

/**
 * Default validation configuration
 */
export const DEFAULT_VALIDATION_CONFIG = SHIFT_VALIDATION_CONFIG.fixed;
